import {
  useCreateResident,
  useDeleteResident,
  useListResidents,
  useUpdateResident,
  type ResidentIn,
  type ResidentListOut,
  type ResidentOut,
} from '@myrentalmate/api-client';

import { Chip, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router';

import {
  EMAIL_FIELD,
  emailColumn,
  optional,
  optionalNumber,
  PHONE_FIELD,
  phoneColumn,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { StatRow, StatTile } from '../../components/Stats';
import { listResult } from '../../lib/apiData';
import { formatPhone } from '../../lib/contact';
import { useCan } from '../../lib/permissions';
import { useDashboardSummary } from '../../lib/summary';
import { useUnitLabels } from '../properties/useUnitLabels';
import { RecordExtras } from '../records/RecordExtras';
import { PortalAccess } from './PortalAccess';

function toBody(values: FormValues): ResidentIn {
  return {
    first_name: required(values, 'first_name'),
    last_name: required(values, 'last_name'),
    email: optional(values, 'email'),
    phone: optional(values, 'phone'),
    unit_id: optionalNumber(values, 'unit_id'),
    notes: optional(values, 'notes'),
  };
}

function toValues(r: ResidentOut): FormValues {
  return {
    first_name: r.first_name,
    last_name: r.last_name,
    email: r.email ?? '',
    phone: formatPhone(r.phone),
    unit_id: r.unit_id?.toString() ?? '',
    notes: r.notes ?? '',
  };
}

function useRows({ q, page, pageSize }: ListParams) {
  return listResult(useListResidents({ q, page, page_size: pageSize }));
}

export function ResidentsPage() {
  const canManage = useCan('residents.manage');
  const create = useCreateResident();
  const update = useUpdateResident();
  const remove = useDeleteResident();
  const unitLabels = useUnitLabels();
  const summary = useDashboardSummary();

  const fields: Field[] = [
    { name: 'first_name', label: 'First Name', required: true },
    { name: 'last_name', label: 'Last Name', required: true },
    EMAIL_FIELD,
    PHONE_FIELD,
    {
      name: 'unit_id',
      label: 'Unit',
      type: 'select',
      options: [
        { value: '', label: 'No unit' },
        ...[...unitLabels].map(([id, label]) => ({ value: String(id), label })),
      ],
    },
    { name: 'notes', label: 'Notes', type: 'multiline' },
  ];

  const columns: Column<ResidentListOut>[] = [
    { label: 'Name', render: (r) => `${r.last_name}, ${r.first_name}` },
    {
      label: 'Unit',
      render: (r) =>
        r.property_id === null ? (
          <Chip size="small" color="warning" label="No Unit" />
        ) : (
          <MuiLink component={Link} to={`/staff/properties/${r.property_id}`}>
            {r.property_name} · {r.unit_number}
          </MuiLink>
        ),
    },
    emailColumn(),
    phoneColumn(),
    {
      label: 'Portal',
      render: (r) =>
        r.user_id === null ? (
          <Typography variant="body2" color="text.secondary">
            Not joined
          </Typography>
        ) : (
          <Chip size="small" color="success" label="Active" />
        ),
    },
  ];

  return (
    <RecordPage
      title="Residents"
      description="Everyone renting from you, and how to reach them."
      summary={
        <StatRow>
          <StatTile label="Residents" value={summary?.residents} />
          <StatTile
            label="In a Unit"
            value={summary && summary.residents - summary.residents_without_unit}
          />
          <StatTile label="No Unit Yet" value={summary?.residents_without_unit} />
          <StatTile label="Vacant Units" value={summary?.vacant_units} />
        </StatRow>
      }
      singular="resident"
      fields={fields}
      columns={columns}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) => update.mutateAsync({ recordId, data: toBody(values) })}
      remove={(recordId) => remove.mutateAsync({ recordId })}
      renderExtras={(row) => (
        <>
          {canManage && <PortalAccess resident={row} />}
          <RecordExtras entityType="resident" entityId={row.id} canManage={canManage} />
        </>
      )}
    />
  );
}
